// Five products in one app: School Improvement (SIP), Student Learning, Configuration, Scoring and
// Content. They
// share sign-in and this shell, and nothing else — each has its own name, colour, tabs and URL
// space, so a link or a bookmark says which product it is for, and nobody has to guess from the
// screen.
//
// Which products a person is offered comes from the server (`/api/me` -> `products`), and is a UI
// hint only: student work is enforced by the database whatever this file shows.
//
// Configuration is offered to whoever configures something or may share it (`/api/me` says so).
// `offered` still drops any product with no
// tab to open, whatever the server says: an entry in the switcher that opens nothing would be a
// dead link, the same rule a tab follows.
//
// Student Learning is organised around the LEARNER ACTIVITY — one lesson, taught to one section, in
// one iteration (docs/STUDENT_LEARNING.md). Its tabs are scoped to the chosen activity, and that
// activity is part of the URL: `/writing/a/<activityId>/review`. A tab appears here only once its
// screen exists; a sidebar link to a placeholder is a dead link (docs/DESIGN.md, "The shell").

export type Product = "sip" | "writing" | "admin" | "scoring" | "content";

export type Section =
  | "workspace" | "traces" | "evals" | "results" | "graders"   // School Improvement
  | "folders" | "review" | "release" | "plan" | "glossary"      // Student Learning
  | "access" | "people" | "districts" | "schools" | "content"    // Configuration
  | "platform" | "roles"
  | "rubrics" | "raters" | "fit-gates" | "scorers" | "models" | "sets" | "experiments"  // Scoring (rubrics: Content too)
  | "curricula" | "traits" | "tasks";                            // Content

export type Scope = "activity" | "product";
// `reference` sits below a line in the sidebar: pages that explain and change nothing
// (docs/DESIGN.md, "The left nav").
export type Group = "main" | "admin" | "reference";

export interface TabSpec {
  section: Section;
  label: string;
  // `activity` tabs live at /writing/a/<activityId>/<section>; `product` tabs at /<product>/<section>.
  scope: Scope;
  // Which sidebar heading it sits under. `main` has no heading.
  group: Group;
  // Hidden unless /api/me says the caller is an operator. A UI hint, never a permission.
  adminOnly?: boolean;
  // Hidden when the current activity's lesson has no task — a lesson taught and not scored.
  scoredOnly?: boolean;
  // Reachable by URL, absent from the sidebar. For a screen you need at a particular moment and
  // never otherwise: "How work arrived" answers "why are there 22 papers for 26 students", which
  // is a question you have when the counts disagree and at no other time. As a permanent tab it
  // advertised a stage of our pipeline as if it were a place a teacher goes.
  hidden?: boolean;
}

interface ProductSpec {
  label: string;
  blurb: string;
  tabs: TabSpec[];
  // Headings for groups other than `main`.
  groups: Partial<Record<Group, string>>;
}

export const PRODUCTS: Record<Product, ProductSpec> = {
  sip: {
    label: "School Improvement",
    blurb: "Plans, goals and progress across schools",
    tabs: [
      { section: "workspace", label: "Workspace", scope: "product", group: "main" },
      { section: "traces", label: "Traces", scope: "product", group: "admin", adminOnly: true },
      { section: "evals", label: "Evals", scope: "product", group: "admin", adminOnly: true },
      { section: "results", label: "Results", scope: "product", group: "admin", adminOnly: true },
      { section: "graders", label: "Graders", scope: "product", group: "admin", adminOnly: true },
    ],
    groups: { admin: "Admin" },
  },
  writing: {
    label: "Student Learning",
    blurb: "Collect, score, review and release student writing",
    // Home first, and it is the only class-scoped tab: what needs a human now, above where the
    // class has got to in the plan. The rest are about ONE assignment.
    //
    // The old order named our pipeline — Student work, How work arrived, Release are `artifact`,
    // `intake_manifest` and `artifact_delivery` in friendlier words — and had no place at all for
    // the curriculum plan, which is what the scores are supposed to change. A nav that cannot
    // reach the plan cannot express the product.
    // NO TABS. Student Learning's sidebar is the curriculum plan (shell/PlanNav.tsx) — class,
    // unit, lesson — because a teacher's year is a sequence of lessons and "where am I" is a
    // position in it. Choosing a lesson is choosing the assignment, so the activity picker went
    // away with the tabs: nothing here is named twice.
    //
    // These three still route. `review` is what a lesson opens: the student work for it.
    // `folders` answers "why 22 papers for 26 students", which you ask when the counts disagree
    // and never otherwise. `release` is the measurement view of a gate that is otherwise a button
    // at the end of reviewing — it was a destination, and a destination is what made it look like
    // a stage a teacher walks through rather than the last thing they do.
    tabs: [
      { section: "review", label: "Student work", scope: "activity", group: "main", hidden: true },
      { section: "folders", label: "How work arrived", scope: "activity", group: "main",
        hidden: true },
      { section: "release", label: "Release", scope: "activity", group: "main",
        adminOnly: true, hidden: true },
      // A unit or a lesson: what it asks and what it is, at /writing/u/<id> and /writing/l/<id>.
      // Reached from the plan in the sidebar, never from a tab strip.
      { section: "plan", label: "Plan", scope: "product", group: "main", hidden: true },
      // The admin reference: what everything is, and what it is not. HIDDEN like its siblings,
      // because Student Learning has no tab strip at all — `Shell` gives this product the curriculum
      // plan as its whole sidebar, so a TabSpec here can never be a nav item however it is
      // declared. It is reached from the account menu; the entry stays because routing needs it.
      //
      // Admin-only: a teacher meets these words one at a time through the tooltips on the words
      // themselves, and twenty-one definitions in their sidebar would advertise our internals as
      // somewhere they ought to go.
      { section: "glossary", label: "Glossary", scope: "product", group: "main",
        adminOnly: true, hidden: true },
    ],
    groups: {},
  },
  // Configuring districts, schools, content providers and the platform: who reaches what, the
  // people, and the organizations (the Access design). Not `adminOnly`: that flag is for
  // operators, and configurators and editors use this too. Who may see each screen, and what they
  // may do there, is the server's answer.
  admin: {
    label: "Configuration",
    blurb: "Who reaches what, people, districts, schools, content providers and the platform",
    tabs: [
      // Who reaches what, by scope or by person; giving and ending access; cohorts and classes.
      { section: "access", label: "Access", scope: "product", group: "main" },
      // Everyone who has signed in or been invited: where they belong, and who they are.
      { section: "people", label: "People", scope: "product", group: "main" },
      // The four kinds of organization (the Access Map plan, change 4a): districts and the real
      // schools each owns, every school, content providers and their teams, and the platform with
      // every tenant listed as information.
      { section: "districts", label: "Districts", scope: "product", group: "main" },
      { section: "schools", label: "Schools", scope: "product", group: "main" },
      { section: "content", label: "Content providers", scope: "product", group: "main" },
      { section: "platform", label: "Platform", scope: "product", group: "main" },
      // Reference: what each role lets someone do, where it is given, and who gives it.
      { section: "roles", label: "Roles", scope: "product", group: "reference" },
    ],
    groups: {},
  },
  // How a trait is scored and whether a rater agrees with people (docs/SCORING_WORKBENCH.md).
  // Platform-only, and read-only for now. No papers are shown here: a paper is a student's work,
  // and this product is about the rater.
  scoring: {
    label: "Scoring",
    blurb: "How each trait is scored, and how closely the rater agrees with people",
    tabs: [
      { section: "rubrics", label: "Rubrics", scope: "product", group: "main" },
      { section: "raters", label: "Raters", scope: "product", group: "main" },
      { section: "fit-gates", label: "Fit gates", scope: "product", group: "main" },
      { section: "scorers", label: "Scorers", scope: "product", group: "main" },
      { section: "models", label: "Models", scope: "product", group: "main" },
      { section: "sets", label: "Evaluation sets", scope: "product", group: "main" },
      { section: "experiments", label: "Experiments", scope: "product", group: "main" },
    ],
    groups: {},
  },
  // What a content provider writes, publishes and licenses: curricula, the rubrics its lessons
  // are scored on and their traits, and the tasks students are asked to do (the Content design). Offered to whoever
  // sees a provider's content; what each may change is the server's answer. `rubrics` is Scoring's
  // section name too: the URL says which product, and App draws Content's for /content/rubrics.
  content: {
    label: "Content",
    blurb: "Write, publish and license curricula, rubrics and tasks",
    tabs: [
      { section: "curricula", label: "Curricula", scope: "product", group: "main" },
      { section: "rubrics", label: "Rubrics", scope: "product", group: "main" },
      { section: "traits", label: "Traits", scope: "product", group: "main" },
      { section: "tasks", label: "Tasks", scope: "product", group: "main" },
    ],
    groups: {},
  },
};

export const SECTION_LABEL: Record<Section, string> = Object.fromEntries(
  Object.values(PRODUCTS).flatMap((p) => p.tabs.map((t) => [t.section, t.label])),
) as Record<Section, string>;

export const ORDER: Product[] = ["sip", "writing", "admin", "scoring", "content"];

export function isProduct(v: unknown): v is Product {
  return ORDER.includes(v as Product);
}

export function tabFor(product: Product, section: Section): TabSpec | undefined {
  return PRODUCTS[product].tabs.find((t) => t.section === section);
}

/** The tabs one product shows this person, in sidebar order. */
export function tabsFor(product: Product, isAdmin: boolean, scored = true): TabSpec[] {
  return PRODUCTS[product].tabs.filter(
    (t) => (isAdmin || !t.adminOnly) && (scored || !t.scoredOnly));
}

/** The tabs the SIDEBAR shows: routable ones, minus those reachable only by URL. */
export function navTabsFor(product: Product, isAdmin: boolean, scored = true): TabSpec[] {
  return tabsFor(product, isAdmin, scored).filter((t) => !t.hidden);
}

/** The sections one product shows this person. Includes hidden tabs, because this is what
 *  ROUTING resolves against: a demoted screen still has to answer at its URL. */
export function sectionsFor(product: Product, isAdmin: boolean): Section[] {
  return tabsFor(product, isAdmin).map((t) => t.section);
}

/** Keep only products the server named, in switcher order. Unknown names are dropped, and so is
 *  a product with no tab to open — routing into it would have no first tab to land on. */
export function offered(fromServer: unknown): Product[] {
  const names = Array.isArray(fromServer) ? fromServer : [];
  return ORDER.filter((p) => names.includes(p) && PRODUCTS[p].tabs.length > 0);
}

/** A node of the curriculum plan: a unit or a lesson. */
export type PlanKind = "unit" | "lesson";

export interface Place {
  product: Product;
  section: Section;
  // Present only on a plan URL. `kind` decides the path letter, so a unit and a lesson that
  // shared an id would still be different places.
  planKind?: PlanKind;
  planId?: string;
  // Present only on an activity-scoped Writing URL. Absent — not null — otherwise, so a place
  // with no activity compares equal to one written before activities existed.
  activityId?: string;
}

/**
 * Where a URL path puts you. `/writing/a/<id>/review` and `/writing/review` are exact;
 * `/writing` is that product's first tab; anything else — `/`, an old bookmark, a product you are
 * not offered, an admin tab you cannot see — falls back to `fallback`'s first tab rather than to
 * an empty or forbidden screen.
 */
export function placeFromPath(
  path: string, available: Product[], isAdmin: boolean, fallback: Product,
): Place {
  const parts = path.split("/").filter(Boolean);
  let [first, second] = parts;
  let activityId: string | undefined;
  if (first === "writing" && second === "a" && parts[2]) {
    activityId = decodeURIComponent(parts[2]);
    second = parts[3];
  }
  const product = isProduct(first) && available.includes(first) ? first : fallback;
  // A unit or a lesson. Checked before the tab lookup, because `u` and `l` are not sections and
  // would otherwise fall through to the product's first tab and silently lose the id.
  if (product === "writing" && first === "writing" && (second === "u" || second === "l")
      && parts[2]) {
    return { product, section: "plan", planKind: second === "u" ? "unit" : "lesson",
             planId: decodeURIComponent(parts[2]) };
  }
  const tabs = sectionsFor(product, isAdmin);
  const section = product === first && tabs.includes(second as Section)
    ? (second as Section) : tabs[0];
  return product === first && activityId ? { product, section, activityId } : { product, section };
}

export function pathFor(place: Place): string {
  if (place.section === "plan" && place.planId && place.planKind) {
    return `/${place.product}/${place.planKind === "unit" ? "u" : "l"}/`
         + encodeURIComponent(place.planId);
  }
  const tab = tabFor(place.product, place.section);
  if (place.activityId && tab?.scope === "activity") {
    return `/${place.product}/a/${encodeURIComponent(place.activityId)}/${place.section}`;
  }
  return `/${place.product}/${place.section}`;
}

/**
 * What a path ASKED for, before any fallback: the product and tab it names, if both exist.
 * placeFromPath answers "where should this person be"; this answers "where did they try to go",
 * which is what lets an admin tab opened by a non-admin say so instead of quietly showing another.
 */
export function requestedTab(path: string): { product: Product; section: Section } | null {
  const parts = path.split("/").filter(Boolean);
  const [first] = parts;
  const second = first === "writing" && parts[1] === "a" ? parts[3] : parts[1];
  if (!isProduct(first) || !second) return null;
  const tab = tabFor(first, second as Section);
  return tab ? { product: first, section: tab.section } : null;
}
